/**
 * author: Soumya Shreyas
 */
import moment from "moment";
import React, { useCallback, useEffect, useState, useRef } from "react";
import { Button, Dropdown, Form, Table } from "react-bootstrap";
import "react-datepicker/dist/react-datepicker.css";
import { withTranslation } from "react-i18next";
import {
  BiDotsHorizontalRounded, BiSearch,
  BiSort,
  BiPlus, 
  BiPencil
} from "react-icons/bi";
import { FcFinePrint } from "react-icons/fc";

import { Link, useNavigate } from "react-router-dom";
import Pagination from "../../../components/Pagination/Pagination";
import EnabledOnlyFilter from "../../../components/EnabledOnlyFilter/EnabledOnlyFilter";
import {
  DEFAULT_ITEMS_PER_PAGE,
  LIST_DEF_SORT,
  SORT_ORDER_DESC
} from "../../../core/constants/common";
import UserService from "../../../core/service/UserService";
import Utils from '../../../core/utilities/Utils';
import nodataimg from "../../../images/nodatafound.png";
import { Modal } from "react-bootstrap";
import UserForm from "./UserForm";
import { AiOutlineUsergroupAdd } from "react-icons/ai";
import './UserList.scss';
import useHasPermission from "../../../core/hooks/useHasPermission";

function UserList({ t }) {
  const navigate = useNavigate();

  const [itemsTotalCount, setItemTotalCount] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(1);
  const [order, setOrder] = useState(SORT_ORDER_DESC);
  const [sortField, setSortField] = useState(LIST_DEF_SORT);
  const [searchKey, setSearchKey] = useState("");
  const [viewFilter, setViewFilter] = useState('enabled');
  const [users, setUsers] = useState([]);
  const [show, setShow] = useState(false)
  const [showRolesModal, setShowRolesModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const { hasUserUpdatePermission, isSuperAdmin } = useHasPermission();


  const listUsers = useCallback((
    pageNumber,
    itemsPerPageVal,
    searchKeyWord,
    sortFieldName
  ) => {
    if (pageNumber > 0) {
      pageNumber = pageNumber - 1;
    } else {
      setCurrentPage(1);
    }
    let perPage;
    if (itemsPerPageVal > 0) {
      perPage = itemsPerPageVal;
    } else {
      perPage = itemsPerPage;
    }
    var sort = sortFieldName + "," + order;
    const statusParam = viewFilter === 'enabled' ? 'active' : undefined;
    UserService.getUsers(
      pageNumber,
      perPage,
      searchKeyWord,
      sort,
      false,
      [],
      statusParam
    ).then((response) => {
      var totalPages = parseFloat(response.headers["x-total-count"]);
      setItemTotalCount(totalPages);
      setUsers([...response.data]);
    }).catch((error) => {
    });
  }, [itemsPerPage, order, viewFilter]);

  useEffect(() => {
    listUsers(0, itemsPerPage, searchKey, sortField);
  }, [listUsers, searchKey, sortField, itemsPerPage, viewFilter]);


  const handleSort = (val) => {
    if (sortField === val && order === "asc") {
      setOrder("desc");
    } else {
      setOrder("asc");
    }
    setSortField(val);
  };

  const handleSearch = (e) => {
    const key = e.target.value;
    setSearchKey(key);
  };
  const handleClose = () => { setShow(false) };
  const handleNewUserClick = () => { setShow(true) };

  const handleActionClick = (user, action) => {
    navigate('../user_form?login=' + user.login);
  };

  const getRolesToDisplay = (roles) => {
    let text = '';
    roles?.forEach((role, index) => {
      text += Utils.getUserRoleText(role);
      if (index !== roles.length - 1) {
        text += ' ,';
      }
    });
    return text;
  };

  const renderModal = () => (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">{t("CreateUser.label")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <UserForm
          show={show}
          onHide={handleClose}
          onSaveUser={() => {
            listUsers(0, itemsPerPage, searchKey, sortField);
            setShow(false);
          }}
        />
      </Modal.Body>

    </Modal>
  );

  const handleCloseRolesModal = () => {
    setShowRolesModal(false);
    setSelectedUser(null);
  };

  const handleShowRolesModal = (user) => {
    setSelectedUser(user);
    setShowRolesModal(true);
  };


  // Add the roles modal render function
  const renderRolesModal = () => (
    <Modal
      show={showRolesModal}
      onHide={handleCloseRolesModal}
      size="md"
      aria-labelledby="roles-modal-title"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title as="h5" id="roles-modal-title">
          {t("Roles.label")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {selectedUser?.groups?.length > 0 ? (
          <div className="roles-container">

            {selectedUser.groups
              .map(g => (
                <span key={g} className="badge bg-secondary">
                  {g}
                </span>
              ))}

          </div>
        ) : (
          <p>{t("NoRolesToDisplay.label")}</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCloseRolesModal}>
          {t("Close.label")}
        </Button>
      </Modal.Footer>
    </Modal>
  );




  return (
    <>
      <div className="FormsList">
        <div className="List_header">
          <h5 className="MH">
            {t("Users.label")}
          </h5>
          <div className="List_sorts_menu d-flex gap-2 align-items-center">
            <EnabledOnlyFilter
              viewFilter={viewFilter}
              onViewFilterChange={setViewFilter}
              dropdownId="user-view-filter-dropdown"
            />
            <Button variant="primary" hidden={!hasUserUpdatePermission() && !isSuperAdmin()} onClick={() => handleNewUserClick()}>
              <BiPlus className="me-1" />
              {t("New.label")}
            </Button>
          </div>
        </div>

        <div className="List_Table_01">
          <div className="List_table-card01_head">
            <div className="List_table-card_Search_01">
              <div className="List_table-card_Search_Box">
                <Form>
                  <input
                    type="text"
                    className="form-control"
                    value={searchKey}
                    onChange={handleSearch}
                    placeholder={t("SearchByUsers.label")}
                  />
                  <Link to="#" className="Search_iocnBox">
                    <BiSearch />
                  </Link>
                </Form>
              </div>
            </div>

          </div>
          <div className="card List_table-card01">
            <Table responsive="sm">
              <thead>
                <tr>
                  <th className="th_check">
                    <Form.Check
                      inline
                      name="group1"
                      type="checkbox"
                      id="inline-checkbox-1"
                    />
                  </th>
                  <th>
                    <Link to="#" onClick={() => handleSort("name")}>
                      {t("Name.label")}
                      <BiSort />
                    </Link>
                  </th>
                  <th>
                    <Link to="#" onClick={() => handleSort("createdDate")}>
                      {t("CreatedDate.label")}
                      <BiSort />
                    </Link>
                  </th>
                  <th>{t("Email.label")}</th>
                  <th>{t("Status.label")}</th>
                  <th>{t("Roles.label")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {users?.length > 0
                  ?
                  (
                    users?.map((user, index) => (
                      <tr key={user.id}>
                        <td className="checkbox">
                          {" "}
                          <Form.Check
                            inline
                            name="group1"
                            type="checkbox"
                            id="inline-checkbox-1" />
                        </td>
                        <td>{user.name}</td>
                        <td>{user.createdDate ? moment(user.createdDate).format("MMM DD, YYYY") : ""}</td>
                        <td>{user.email}</td>
                        <td>
                          <span className={`badge ${user.activated ? 'bg-success' : 'bg-secondary'}`}>
                            {user.activated ? t('Enabled.label') : t('Disabled.label')}
                          </span>
                        </td>
                        <td>
                          <AiOutlineUsergroupAdd
                            style={{ cursor: 'pointer' }} // Add a pointer cursor to indicate it's clickable
                            onClick={() => handleShowRolesModal(user)}
                          />
                        </td>

                        <td hidden={!hasUserUpdatePermission() && !isSuperAdmin()}>
                          <Dropdown>
                            <Dropdown.Toggle
                              variant="blank_outline"
                              id="user-list-dropdown">
                              <BiDotsHorizontalRounded />
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item onClick={() => handleActionClick(user, "EDIT")}>
                                <BiPencil className="me-2" />
                                {t("Edit.label")}
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center">
                        <div className="Nodatafound_box">
                          <h6><FcFinePrint className="me-2" /> {t("Nodatafound.label")}</h6>
                        </div>
                      </td>
                    </tr>
                  )}
              </tbody>
            </Table>
            <div className="p-2 List_footer_count">
              <Pagination
                itemsTotalCount={itemsTotalCount}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                alwaysShown={true}
                setItemsPerPage={setItemsPerPage}
                pageClicked={(pageNum, perPage) => {
                  listUsers(pageNum, perPage, searchKey, sortField);
                }}
              />
            </div>
            {renderModal()}
            {renderRolesModal()}
          </div>
        </div>
      </div>
    </>
  );
}
UserList.propTypes = {};

export default withTranslation()(UserList);
